import React, { useEffect, useState } from 'react'
import { useStores } from '@trivie/core'
import { UserCertificationMedals } from '@trivie/core/src/models/UserCertificationMedal'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { FlatList, Platform, TouchableOpacity, View, ViewStyle } from 'react-native'
import { CertificationCard, MedalGroupCard } from '../card'
import { CertificationModal } from '../certification-modal'
import { MedalModal } from '../medal-modal'
import { Skeleton } from '../skeleton'
import { Text } from '../text'

const groupBy = (key: string | number) => (array: any[]) =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key]
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj)
    return objectsByKeyValue
  }, {})

interface CertsMedalsProps {
  data: Instance<typeof UserCertificationMedals> | null
  loading?: boolean
}
export const CertsMedals = observer((props: CertsMedalsProps) => {
  const [showMedals, setShowMedals] = useState(true)
  const [showCertifications, setShowCertifications] = useState(true)
  const [selectedCert, setSelectedCert] = useState<any>(null)
  const [selectedMedal, setSelectedMedal] = useState<any>(null)
  const [medalGroups, setMedalGroups] = useState<any>({})

  const { userStore } = useStores()
  const { getCertificationDetail, user, certificationDetail } = userStore

  const { data, loading } = props

  useEffect(() => {
    const _medalGroups = groupBy('level')(data?.medals || [])
    setMedalGroups(_medalGroups)

    setShowCertifications((data?.certifications?.length || 0) > 0)
    setShowMedals((data?.medals?.length || 0) > 0)
  }, [data])

  const [showMedalModal, setShowMedalModal] = useState(false)
  const [showCertModal, setShowCertModal] = useState(false)

  const handleCloseCert = () => setShowCertModal(false)
  const handleCloseMedal = () => setShowMedalModal(false)

  const onPressCert = (item: any) => {
    if (user) {
      getCertificationDetail(user?.id, item.certification_id).then((resp) => {
        setSelectedCert(item)
        setShowCertModal(true)
      })
    }
  }

  const renderMedalGroups = () => {
    return (
      <FlatList
        data={Object.keys(medalGroups)
          .slice()
          .sort((a, b) => {
            return a == 'Gold' ? -1 : b == 'Gold' ? 1 : a > b ? -1 : 1
          })}
        horizontal
        contentContainerStyle={LIST_CONTAINER}
        renderItem={({ item }) => {
          return (
            <MedalGroupCard
              style={{ marginRight: 20 }}
              level={item}
              count={medalGroups[item].length}
              title={item}
              onPress={() => {
                setSelectedMedal(item)
                setShowMedalModal(true)
              }}
            />
          )
        }}
      />
    )
  }

  const renderCertifications = () => {
    const sortedByLevelType = data?.certifications
      ?.slice()
      .sort((a, b) => (a.level == 'Gold' ? -1 : b.level == 'Gold' ? 1 : a.level > b.level ? -1 : 1))
    return (
      <FlatList
        data={[...sortedByLevelType]}
        horizontal
        contentContainerStyle={LIST_CONTAINER}
        renderItem={({ item }) => {
          return (
            <CertificationCard
              style={{ marginRight: 20 }}
              onPress={() => onPressCert(item)}
              certification={item.level}
              recall={item.recall}
              title={item.title}
            />
          )
        }}
      />
    )
  }

  return loading ? (
    <View style={{ flexDirection: 'row', paddingHorizontal: 12 }}>
      <Skeleton style={LOADER} />
      <Skeleton style={LOADER} />
      <Skeleton style={LOADER} />
    </View>
  ) : data?.medals &&
    data?.certifications &&
    (data?.medals?.length > 0 || data?.certifications?.length > 0) ? (
    <>
      <MedalModal
        visible={showMedalModal}
        onClose={handleCloseMedal}
        level={selectedMedal}
        items={medalGroups[selectedMedal]}
      />
      <CertificationModal
        visible={showCertModal}
        onClose={handleCloseCert}
        title={selectedCert?.title}
        level={selectedCert?.level}
        criteria={certificationDetail?.criteria_met ?? ''}
        earned={certificationDetail?.datetime_awarded ?? ''}
        recall={selectedCert?.recall}
      />
      <View style={HEADER}>
        {showCertifications && (
          <>
            <View style={{ flexDirection: 'row' }}>
              <Text
                style={{
                  color: '#9098A3',
                  fontWeight: '700',
                  marginRight: 4,
                }}
                variant="body2Emphasized"
                uppercase
                tx="Certifications.header"
              />
              <Text
                style={{ color: '#9098A370', fontWeight: '700' }}
                variant="body2Emphasized"
                text={String(data?.certifications.length)}
              />
            </View>
            {renderCertifications()}
          </>
        )}
        {showMedals && (
          <View style={{ marginBottom: 15 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text
                style={{ color: '#9098A3', fontWeight: '700', marginRight: 4 }}
                variant="body2Emphasized"
                uppercase
                tx="Certifications.medals"
              />
              <Text
                style={{ color: '#9098A370', fontWeight: '700' }}
                variant="body2Emphasized"
                text={String(data?.medals.length)}
              />
            </View>

            {renderMedalGroups()}
          </View>
        )}
      </View>
    </>
  ) : null
})

const HEADER: ViewStyle = {
  marginTop: 10,
  paddingHorizontal: Platform.OS === 'web' ? 0 : 8,
}
const LOADER: ViewStyle = {
  width: 150,
  height: 143,
  marginBottom: 16,
  marginRight: 20,
  borderRadius: 20,
}
const LIST_CONTAINER: ViewStyle = {
  paddingBottom: 12,
  marginBottom: 6,
  marginLeft: 10,
  marginTop: 10,
}
